import { cn } from '@ibe/ui';
import { type LucideIcon } from 'lucide-react';

/**
 * One figure on a summary screen.
 *
 * Reusable because every module gets a summary eventually — occupancy on a
 * property, outstanding balance on a reservation, queue depth on integrations
 * — and four hand-rolled variants of "a number in a box" is how a dashboard
 * ends up with four different label sizes.
 *
 * Everything is a token: `text-caption`, `text-page-title`, `shadow-card`,
 * `rounded-lg`. No literal sizes or colours, so a change to the scale reaches
 * every tile at once.
 */

export const STAT_TONE = {
  NEUTRAL: 'NEUTRAL',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  DANGER: 'DANGER',
} as const;

export type StatTone = (typeof STAT_TONE)[keyof typeof STAT_TONE];

const ICON_TONE: Readonly<Record<StatTone, string>> = {
  NEUTRAL: 'bg-primary/15 text-primary',
  SUCCESS: 'bg-success/15 text-success-foreground',
  WARNING: 'bg-warning/15 text-warning-foreground',
  DANGER: 'bg-destructive/15 text-destructive',
};

export interface StatCardProps {
  readonly label: string;
  readonly value: string;
  readonly icon: LucideIcon;
  readonly tone?: StatTone;
  /**
   * A qualifier under the value — "of 24 sellable", "vs. yesterday".
   *
   * Deliberately a plain string rather than a trend widget. A tile that can
   * render an arrow will get one whether or not the API returns a comparison,
   * and a made-up trend is worse than none.
   */
  readonly hint?: string;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  tone = STAT_TONE.NEUTRAL,
  hint,
}: StatCardProps): React.JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* `dt`/`dd` because the grid this sits in is a description list:
              a screen reader then reads "Arrivals, 12" as one pair rather
              than two unrelated strings. */}
          <dt className="text-caption uppercase tracking-wide text-text-subtle">{label}</dt>
          <dd className="mt-1 truncate text-2xl font-bold tracking-tight">{value}</dd>
          {hint === undefined ? null : (
            <p className="mt-0.5 text-caption text-muted-foreground">{hint}</p>
          )}
        </div>

        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
            ICON_TONE[tone],
          )}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

/**
 * The tile's own loading shape.
 *
 * A skeleton rather than a spinner, and the same height as the real tile: a
 * dashboard that collapses to one line and then jumps back to four cards makes
 * the whole page move under the reader every time it refetches.
 */
export function StatCardSkeleton(): React.JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="w-full space-y-2">
          <div className="h-3 w-20 animate-pulse rounded bg-muted" />
          <div className="h-7 w-16 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-muted" />
      </div>
    </div>
  );
}
